/**
 * News & Guides hub (sections/news-hub.liquid).
 *
 * Every article on the page is already in the grid; this filters and sorts them in place: a search box, a News /
 * Guides toggle, game chips and a sort menu. Only the first few matching cards show, and "Load more" reveals the
 * next batch. Without JavaScript all the cards simply show.
 */

class NewsHub extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-nh-list]');
    if (!this.list) return;

    this.cards = [...this.list.querySelectorAll('[data-nh-card]')];
    this.perLoad = Number(this.dataset.perLoad) || 9;
    this.shown = this.perLoad;
    this.state = { type: 'all', game: 'all', query: '' };

    this.count = this.querySelector('[data-nh-count]');
    this.empty = this.querySelector('[data-nh-empty]');
    this.more = this.querySelector('[data-nh-more]');

    let timer = 0;
    this.querySelector('[data-nh-search]')?.addEventListener('input', (event) => {
      clearTimeout(timer);
      timer = window.setTimeout(() => {
        this.state.query = /** @type {HTMLInputElement} */ (event.target).value.trim().toLowerCase();
        this.update(true);
      }, 150);
    });

    this.querySelectorAll('[data-nh-type]').forEach((button) =>
      button.addEventListener('click', () => this.choose('type', /** @type {HTMLElement} */ (button)))
    );
    this.querySelectorAll('[data-nh-game]').forEach((button) =>
      button.addEventListener('click', () => this.choose('game', /** @type {HTMLElement} */ (button)))
    );

    this.querySelector('[data-nh-sort]')?.addEventListener('change', (event) => {
      this.sort(/** @type {HTMLSelectElement} */ (event.target).value);
      this.update(true);
    });

    this.more?.addEventListener('click', () => {
      this.shown += this.perLoad;
      this.update(false);
    });

    this.update(true);
  }

  /**
   * @param {'type' | 'game'} key
   * @param {HTMLElement} button
   */
  choose(key, button) {
    const attribute = key === 'type' ? 'data-nh-type' : 'data-nh-game';
    this.querySelectorAll(`[${attribute}]`).forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
    this.state[key] = button.getAttribute(attribute) || 'all';
    this.update(true);
  }

  /** @param {string} order */
  sort(order) {
    const byDate = (/** @type {HTMLElement} */ a, /** @type {HTMLElement} */ b) =>
      Number(b.dataset.date) - Number(a.dataset.date);
    const sorters = {
      newest: byDate,
      oldest: (/** @type {HTMLElement} */ a, /** @type {HTMLElement} */ b) => -byDate(a, b),
      az: (/** @type {HTMLElement} */ a, /** @type {HTMLElement} */ b) =>
        (a.dataset.title || '').localeCompare(b.dataset.title || ''),
    };
    this.cards.sort(sorters[/** @type {keyof typeof sorters} */ (order)] || byDate);
    this.cards.forEach((card) => this.list?.append(card));
  }

  /** @param {HTMLElement} card */
  matches(card) {
    const { type, game, query } = this.state;
    if (type !== 'all' && card.dataset.type !== type) return false;
    if (game !== 'all' && !(card.dataset.games || '').split(' ').includes(game)) return false;
    if (query && !`${card.dataset.title} ${card.dataset.text}`.includes(query)) return false;
    return true;
  }

  /** @param {boolean} reset - Start again from the first batch */
  update(reset) {
    if (reset) this.shown = this.perLoad;
    const matching = this.cards.filter((card) => this.matches(card));

    this.cards.forEach((card) => {
      const index = matching.indexOf(card);
      const visible = index !== -1 && index < this.shown;
      if (visible && card.hidden) card.classList.add('nh-card--in');
      card.hidden = !visible;
    });

    if (this.count) {
      const total = matching.length;
      this.count.textContent = `${total} ${total === 1 ? 'article' : 'articles'}`;
    }
    if (this.empty) this.empty.hidden = matching.length > 0;
    if (this.more) this.more.hidden = matching.length <= this.shown;
  }
}

if (!customElements.get('news-hub')) customElements.define('news-hub', NewsHub);